import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
  Play, Pause, Loader, Save, Download, Trash2, AlertCircle, 
  CheckCircle, Volume2, RefreshCw, ArrowLeft, Mic, MicOff,
  Settings, Edit, Plus, Minus, StopCircle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import API_URL from '../config';
import logger from '../utils/logger';
import Mixpanel from '../utils/mixpanel';
import GuideModal from './GuideModal';
import { useUser } from '../context/UserContext';
import './DesktopComponentStyles.css';

// Maximum allowed script length for audio generation
const MAX_SCRIPT_LENGTH = 4000;

const MentalImagery = () => {
  // === SINGLE HOOK/REF DECLARATIONS ONLY ===
  const navigate = useNavigate();
  const { user } = useUser();

  // Main state and ref declarations for use throughout the component
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [audioUrl, setAudioUrl] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [savedMentalImagery, setSavedMentalImagery] = useState([]);
  const [currentMentalImagery, setCurrentMentalImagery] = useState(null);
  const [scriptInput, setScriptInput] = useState('');
  const [generatingScript, setGeneratingScript] = useState(false);
  const [generatingAudio, setGeneratingAudio] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordedAudio, setRecordedAudio] = useState(null);
  const [title, setTitle] = useState('');
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const [audioSettings, setAudioSettings] = useState({
    voice: 'calm',
    background: 'ambient',
    pace: 'medium',
    duration: 10
  });

  const recordingTimerRef = useRef(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [recordingPermission, setRecordingPermission] = useState(false);
  const [speechSynthesisActive, setSpeechSynthesisActive] = useState(false);
  const speechSynthesisRef = useRef(null);

  // Helper to request recording permission
  const requestRecordingPermission = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setRecordingPermission(true);
      return stream;
    } catch (error) {
      setError('Microphone access denied. Please enable microphone permissions.');
      setRecordingPermission(false);
      return null;
    }
  };

  // Start recording audio
  const startRecording = async () => {
    try {
      logger.debug('Starting audio recording');
      setRecordingTime(0);
      audioChunksRef.current = [];

      // Get the media stream
      const stream = recordingPermission ? 
        await navigator.mediaDevices.getUserMedia({ audio: true }) : 
        await requestRecordingPermission();
      if (!stream) return;

      // Create a new MediaRecorder instance
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;

      // Set up event handlers
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
          logger.debug('Recording data chunk received');
        }
      };

      mediaRecorder.onstop = () => {
        logger.debug('Recording stopped');
        // Combine the audio chunks into a single blob
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const audioUrl = URL.createObjectURL(audioBlob);
        setRecordedAudio(audioUrl);
      };

      // Start the recording
      mediaRecorder.start();
      setIsRecording(true);

      // Start the timer
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prevTime => prevTime + 1);
      }, 1000);

      // Track recording start
      Mixpanel.trackFeatureAction('Mental Imagery', 'Started Recording');
    } catch (error) {
      logger.error('Error starting recording');
      setError('Failed to start recording. Please check your microphone permissions.');
    }
  };

  useEffect(() => {
    // Track page view
    Mixpanel.trackEnhancedPageView('/mental-imagery', 'Mental Imagery', {
      feature: 'Mental Imagery',
      action: 'Opened'
    });
    
    // Check if we have an onboarding mental imagery ID
    const mentalImageryId = localStorage.getItem('onboardingMentalImageryId');
    
    if (mentalImageryId) {
      fetchMentalImageryById(mentalImageryId);
    }
    
    // Fetch all saved mental imagery sessions
    fetchSavedMentalImagery();
    // Instant playback: check for onboardingMentalImageryAudioUrl
    try {
      const onboardingImageryAudioUrl = localStorage.getItem('onboardingMentalImageryAudioUrl');
      if (onboardingImageryAudioUrl) {
        setSavedMentalImagery(prev => {
          // Only add if not already present
          if (!prev.some(m => m.audioUrl === onboardingImageryAudioUrl)) {
            return [
              {
                _id: 'onboarding-latest',
                title: 'Latest Mental Imagery (Onboarding)',
                audioUrl: onboardingImageryAudioUrl,
                script: '',
                createdAt: new Date().toISOString()
              },
              ...prev
            ];
          }
          return prev;
        });
      }
    } catch (err) {
      logger.error('Error adding onboardingMentalImageryAudioUrl for instant playback:', err);
    }
  }, []);

  useEffect(() => {
    fetchSavedMentalImagery();
    // PHASE 2 AUTOMATIC AUDIO GENERATION - TEMPORARILY COMMENTED OUT
    // Users will manually generate audio files when they want them
    // processPendingAudioGeneration();
  }, []);

  // Phase 2: Automatic processing of script-only records
  const processPendingAudioGeneration = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        logger.debug('No authentication token found for Phase 2 processing');
        return;
      }
      
      logger.info('PHASE 2: Starting automatic audio generation for Mental Imagery script-only records');
      
      // Use the batch processing endpoint for Mental Imagery records
      const response = await axios.post(
        `${API_URL}/api/ai/audio/process-pending`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 300000 // 5 minute timeout for batch processing
        }
      );
      
      if (response.data && response.data.successCount > 0) {
        logger.info(`PHASE 2: Successfully generated audio for ${response.data.successCount} Mental Imagery scripts`);
        setMessage(`Generated audio for ${response.data.successCount} Mental Imagery scripts!`);
        
        // Immediately refresh the saved list to show updated records
        await fetchSavedMentalImagery();
        
        // Clear message after a short delay
        setTimeout(() => {
          setMessage('');
        }, 2000);
      } else if (response.data && response.data.totalScripts === 0) {
        logger.debug('PHASE 2: No pending Mental Imagery scripts found for audio generation');
        // Still refresh to ensure we have the latest state from server
        await fetchSavedMentalImagery();
      } else {
        // Always refresh to get the latest state
        await fetchSavedMentalImagery();
      }
      
    } catch (error) {
      // Don't show error to user for automatic processing, just log it
      logger.error('PHASE 2: Error processing pending Mental Imagery audio generation:', error);
      
      // Still try to refresh data in case some processing succeeded
      try {
        await fetchSavedMentalImagery();
      } catch (refreshError) {
        logger.error('Failed to refresh Mental Imagery data after batch processing error:', refreshError);
      }
    }
  };

  // Phase 2: Generate audio for individual script-only record
  const generateAudioForScript = async (scriptId) => {
    setGeneratingAudio(true);
    setError('');
    
    try {
      logger.info(`PHASE 2: Generating audio for Mental Imagery script ID: ${scriptId}`);
      
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found');
      }
      
      // Use the Phase 2 endpoint to generate audio for existing script
      const response = await axios.post(
        `${API_URL}/api/ai/audio/${scriptId}/generate`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 300000 // 5 minute timeout for audio generation
        }
      );
      
      logger.info('PHASE 2: Audio generated successfully for Mental Imagery script');
      
      // Update the saved list with the new audio URL
      setSavedMentalImagery(prevImageries => prevImageries.map(imagery => {
        if (imagery._id === scriptId) {
          return { 
            ...imagery, 
            audioUrl: response.data.audioUrl,
            isScriptOnly: false
          };
        }
        return imagery;
      }));
      
      setMessage('Audio generated successfully! You can now play it.');
      
      // Clear message after 3 seconds
      setTimeout(() => {
        setMessage('');
      }, 3000);
      
    } catch (error) {
      logger.error('PHASE 2: Error generating audio for Mental Imagery script:', error);
      
      // Provide specific error messages
      if (error.response?.status === 404) {
        setError('Script not found or already has audio.');
        // Refresh the saved data to sync UI state with backend
        logger.info('PHASE 2: Refreshing Mental Imagery data due to 404 - script may have been processed already');
        fetchSavedMentalImagery();
      } else if (error.response?.status === 400) {
        setError('Script is too long or invalid for audio generation.');
      } else if (error.code === 'ECONNABORTED') {
        setError('Audio generation timed out. Please try again.');
      } else {
        setError('Failed to generate audio. Please try again later.');
      }
    } finally {
      setGeneratingAudio(false);
    }
  };

  // Fetch saved mental imagery sessions
  const fetchSavedMentalImagery = async () => {
    try {
      setLoading(true);
      setError('');
      
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication required. Please log in again.');
        setLoading(false);
        return;
      }
      
      const response = await axios.get(`${API_URL}/api/ai/mental-imagery`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSavedMentalImagery(response.data);
    } catch (error) {
      logger.error('Error fetching saved mental imagery:', error);
      setError('Failed to fetch saved mental imagery sessions.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch a specific mental imagery by ID
  const fetchMentalImageryById = async (id) => {
    try {
      setLoading(true);
      setError('');
      
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication required. Please log in again.');
        setLoading(false);
        return;
      }
      
      const response = await axios.get(`${API_URL}/api/ai/mental-imagery/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCurrentMentalImagery(response.data);
      setScriptInput(response.data.script || '');
      setTitle(response.data.title || '');
      
      if (response.data.audioUrl && response.data.audioUrl !== "pending") {
        setAudioUrl(response.data.audioUrl);
      } else {
        setAudioUrl("pending");
      }
      
      if (response.data.settings) {
        setAudioSettings(response.data.settings);
      }
      
      // Track selection
      Mixpanel.trackFeatureAction('Mental Imagery', 'Selected Session', {
        sessionId: id
      });
    } catch (error) {
      logger.error('Error fetching mental imagery:', error);
      setError('Failed to fetch mental imagery session.');
    } finally {
      setLoading(false);
    }
  };

  // Delete a mental imagery session with enhanced error handling and retry logic
  const deleteMentalImageryById = async (id) => {
    try {
      setLoading(true);
      setError('');
      
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication required. Please log in again.');
        setLoading(false);
        return;
      }
      
      // Log the ID being sent for debugging
      console.log('Attempting to delete mental imagery with ID:', id);
      
      // Ensure the ID is valid
      if (!id || id === 'undefined') {
        setError('Invalid mental imagery ID.');
        setLoading(false);
        return;
      }
      
      // Format check for MongoDB ObjectId (24 hex characters)
      const objectIdRegex = /^[0-9a-fA-F]{24}$/;
      if (!objectIdRegex.test(id)) {
        setError(`Invalid ID format: ${id}`);
        setLoading(false);
        return;
      }
      
      try {
        const response = await axios.delete(`${API_URL}/api/ai/mental-imagery/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        console.log('Delete response:', response.data);
        
        // If the deleted session was the current one, reset the current session
        if (currentMentalImagery && currentMentalImagery._id === id) {
          setCurrentMentalImagery(null);
          setScriptInput('');
          setTitle('');
          setAudioUrl('');
        }
        
        // Refresh the list of saved sessions
        await fetchSavedMentalImagery();
        
        // Track deletion
        Mixpanel.trackFeatureAction('Mental Imagery', 'Deleted Session', {
          sessionId: id
        });
        
        setMessage('Mental imagery session deleted successfully!');
        setTimeout(() => setMessage(''), 3000);
      } catch (apiError) {
        console.error('API error during delete:', apiError);
        
        // Enhanced error handling with retry option
        if (apiError.response) {
          if (apiError.response.status === 404) {
            setError('Mental imagery session not found. It may have been already deleted.');
            // Auto-refresh the list to sync with server state
            await fetchSavedMentalImagery();
          } else if (apiError.response.status === 401) {
            setError('You are not authorized to delete this mental imagery session.');
          } else if (apiError.response.status === 400) {
            setError(`Invalid request: ${apiError.response.data.message || 'Bad request format'}`);
          } else {
            setError(`Failed to delete: ${apiError.response.data.message || apiError.response.statusText}`);
          }
        } else if (apiError.request) {
          setError('No response from server. Please check your connection and try again.');
        } else {
          setError(`Failed to delete mental imagery: ${apiError.message}`);
        }
      }
    } catch (error) {
      logger.error('Error in delete function:', error);
      setError(`An unexpected error occurred: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const togglePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const playAudio = () => {
    if (audioRef.current && audioUrl && audioUrl !== "pending") {
      audioRef.current.play()
        .then(() => {
          setIsPlaying(true);
          
          // Track play event
          Mixpanel.trackFeatureAction('Mental Imagery', 'Played Audio', {
            sessionId: currentMentalImagery?._id
          });
        })
        .catch(error => {
          logger.error('Error playing audio:', error);
          setError('Failed to play audio. Please try again.');
        });
    }
  };

  const pauseAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
    
    // Track completion
    Mixpanel.trackFeatureAction('Mental Imagery', 'Completed Audio', {
      sessionId: currentMentalImagery?._id
    });
  };

  const downloadAudio = async () => {
    try {
      if (!audioUrl) {
        setError('No audio available to download.');
        return;
      }

      // Show loading message
      setMessage('Preparing download...');

      // Fetch the audio file
      const response = await fetch(audioUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch audio: ${response.status} ${response.statusText}`);
      }

      // Get the audio blob
      const audioBlob = await response.blob();

      // Create a download link
      const downloadUrl = URL.createObjectURL(audioBlob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `${title || 'mental-imagery'}-${new Date().toISOString().slice(0, 10)}.mp3`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      // Clean up the URL object
      URL.revokeObjectURL(downloadUrl);

      // Track download action
      Mixpanel.trackFeatureAction('Mental Imagery', 'Downloaded Audio');
      
      // Show success message
      setMessage('Audio downloaded successfully!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      logger.error('Error downloading audio:', error);
      setError(`Failed to download audio: ${error.message}`);
    }
  };

  const generateMentalImageryScript = async () => {
    try {
      setGeneratingScript(true);
      setError('');
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication required. Please log in again.');
        setGeneratingScript(false);
        return;
      }
      // Get user data for personalization
      const userData = {
        name: user?.name || 'Athlete',
        sport: user?.sport || 'your sport',
        position: user?.sportPosition || 'athlete',
        birthYear: user?.birthYear || null
      };
      // Get mindset data if available
      let mindsetData = {};
      try {
        const mindsetResponse = await axios.get(`${API_URL}/api/mindset`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (mindsetResponse.data) {
          mindsetData = mindsetResponse.data;
        }
      } catch (error) {
        logger.error('Error fetching mindset data:', error);
      }
      // Prepare keys for the script generation
      const keys = [
        mindsetData.oneWordFocus || 'Focus',
        (mindsetData.affirmations && mindsetData.affirmations[0]) || 'I am confident',
        (mindsetData.beliefs && mindsetData.beliefs[0]) || 'I perform my best under pressure'
      ];
      // Generate the script
      const scriptResponse = await axios.post(
        `${API_URL}/api/ai/generate-mental-imagery`,
        { 
          name: userData.name, 
          sport: userData.sport, 
          position: userData.position, 
          keys, 
          birthYear: userData.birthYear 
        },
        { 
          headers: { Authorization: `Bearer ${token}` },
          timeout: 60000 // 60 seconds timeout for longer script generation
        }
      );
      if (!scriptResponse.data || !scriptResponse.data.script) {
        setError('Failed to generate mental imagery script.');
        setGeneratingScript(false);
        return;
      }
      // Enforce the 4000 character limit when setting the script
      let script = scriptResponse.data.script;
      if (script.length > MAX_SCRIPT_LENGTH) {
        script = script.substring(0, MAX_SCRIPT_LENGTH);
        setError(`Script was too long and has been truncated to ${MAX_SCRIPT_LENGTH} characters for audio compatibility.`);
      }
      setScriptInput(script);
      setTitle(`${userData.sport} ${userData.position} Mental Imagery`);
      // Track successful generation
      Mixpanel.trackFeatureAction('Mental Imagery', 'Generated Script');
      setMessage('Mental imagery script generated successfully!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      logger.error('Error generating mental imagery script:', error);
      setError('Failed to generate mental imagery script. Please try again.');
    } finally {
      setGeneratingScript(false);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      // Track recording end
      Mixpanel.trackFeatureAction('Mental Imagery', 'Stopped Recording');
      
      setMessage('Recording completed!');
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const allowedVoices = ['calm', 'energetic', 'soothing'];

  const generateAudio = async () => {
    try {
      setGeneratingAudio(true);
      setError('');
      
      if (!scriptInput.trim()) {
        setError('Please generate or enter a script first.');
        setGeneratingAudio(false);
        return;
      }
      
      if (!title.trim()) {
        setError('Please enter a title first.');
        setGeneratingAudio(false);
        return;
      }
      
      // Enforce script length before sending to backend
      if (scriptInput.length > MAX_SCRIPT_LENGTH) {
        setError(`Script is too long for audio generation. Please shorten it to ${MAX_SCRIPT_LENGTH} characters or less.`);
        setGeneratingAudio(false);
        return;
      }
      
      // Validate voice setting
      let voice = audioSettings.voice;
      if (!allowedVoices.includes(voice)) {
        // Map 'focused' or any invalid value to 'calm' for safety
        voice = 'calm';
      }
      
      // Get the token for authentication
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication required. Please log in again.');
        setGeneratingAudio(false);
        return;
      }
      
      // Set temporary pending state for UI feedback
      setAudioUrl("pending");
      
      // Prepare the request data
      const requestData = {
        script: scriptInput,
        title: title || 'Mental Imagery Session',
        voice,
        background: audioSettings.background,
        pace: audioSettings.pace,
        duration: audioSettings.duration
      };
      
      // Call the backend API to generate audio
      const response = await axios.post(
        `${API_URL}/api/ai/generate-audio`,
        requestData,
        { 
          headers: { Authorization: `Bearer ${token}` },
          timeout: 120000 // 120 seconds timeout for longer audio generation
        }
      );
      
      if (response.data && response.data.audioUrl) {
        // Set the audio URL from the response
        setAudioUrl(response.data.audioUrl);
        
        // Automatically save both script and audio to database
        try {
          const saveData = {
            title: title,
            script: scriptInput,
            audioUrl: response.data.audioUrl,
            settings: audioSettings,
            isScriptOnly: false, // This is a complete record with audio
            type: 'mental-imagery'
          };
          
          const saveResponse = await axios.post(
            `${API_URL}/api/ai/mental-imagery`,
            saveData,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          // Update current mental imagery and refresh the library
          setCurrentMentalImagery(saveResponse.data);
          await fetchSavedMentalImagery();
          
          // Track successful audio generation and save
          Mixpanel.trackFeatureAction('Mental Imagery', 'Generated and Saved Audio', {
            voiceStyle: voice,
            backgroundSound: audioSettings.background,
            duration: audioSettings.duration
          });
          
          setMessage('Audio generated and saved to library successfully!');
          setTimeout(() => setMessage(''), 3000);
          
        } catch (saveError) {
          logger.error('Error saving generated audio:', saveError);
          // Audio was generated but save failed - still show success for audio generation
          setMessage('Audio generated successfully! Please click "Save to Library" to save it.');
          setTimeout(() => setMessage(''), 5000);
        }
        
      } else {
        throw new Error('No audio URL returned from the server');
      }
    } catch (error) {
      logger.error('Error generating audio:', error);
      setError(`Audio generation failed: ${error.response?.data?.message || error.message}`);
      // Set audioUrl back to empty if it was pending
      if (audioUrl === "pending") {
        setAudioUrl('');
      }
    } finally {
      setGeneratingAudio(false);
    }
  };

  const saveMentalImagery = async () => {
    try {
      setLoading(true);
      setError('');
      
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication required. Please log in again.');
        setLoading(false);
        return;
      }
      
      if (!scriptInput) {
        setError('Please generate or enter a mental imagery script first.');
        setLoading(false);
        return;
      }
      
      // Phase 1: Save script-only first (like Mindset Audio)
      const mentalImageryData = {
        title: title || `Mental Imagery Session - ${new Date().toLocaleDateString()}`,
        script: scriptInput,
        audioUrl: '', // Empty for Phase 1 (script-only)
        settings: audioSettings,
        isScriptOnly: true, // Mark as script-only for Phase 2 processing
        type: 'mental-imagery'
      };
      
      logger.info('PHASE 1: Saving Mental Imagery script-only record', {
        title: mentalImageryData.title,
        scriptLength: scriptInput.length,
        settings: audioSettings
      });
      
      // Save to backend using AudioVisualization model (same as Mindset Audio)
      const saveResponse = await axios.post(
        `${API_URL}/api/ai/mental-imagery`,
        mentalImageryData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update current mental imagery and saved list
      setCurrentMentalImagery(saveResponse.data);
      await fetchSavedMentalImagery();
      
      // Track successful save
      Mixpanel.trackFeatureAction('Mental Imagery', 'Saved Script (Phase 1)');
      
      // Show success message
      setMessage(`Mental imagery script "${mentalImageryData.title}" saved! Audio will be generated automatically when you visit your library.`);
      setTimeout(() => setMessage(''), 5000);
      
      // Store in localStorage for potential Phase 2 processing (like Mindset Audio)
      try {
        localStorage.setItem('mentalImageryScriptId', saveResponse.data._id);
        localStorage.setItem('mentalImagerySettings', JSON.stringify(audioSettings));
        logger.debug('Mental Imagery script ID and settings stored for Phase 2 processing');
      } catch (err) {
        logger.warn('Failed to store Mental Imagery data in localStorage:', err);
      }
      
    } catch (error) {
      logger.error('Error saving mental imagery script:', error);
      // More detailed error message
      if (error.response) {
        setError(`Failed to save: ${error.response.data.message || error.response.statusText || 'Server error'}`);
      } else if (error.request) {
        setError('Failed to save: No response from server. Please check your connection and try again.');
      } else {
        setError(`Failed to save: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Format time for display (MM:SS)
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  };

  // Helper function to determine UI display state
  const getUIState = () => {
    // Check if we have a generated audio that's ready to play
    const hasGeneratedAudio = audioUrl && audioUrl !== 'pending' && !audioUrl.includes('pending');
    
    // For Mental Imagery, we always want to show script creation fields
    // Users should be able to create multiple mental imagery sessions
    // Only hide script fields if we're currently playing a generated audio
    
    return {
      showScriptFields: !hasGeneratedAudio, // Always show unless currently playing generated audio
      showAudioPlayer: hasGeneratedAudio,
      hasExistingAudio: false // Don't use this to hide script creation interface
    };
  };

  const uiState = getUIState();

  return (
    <>
    <GuideModal guideKey="MentalImagery" />
    <div className="w-full desktop-standard-container">
        <div className="flex flex-col md:flex-row gap-6">
          {/* Main Content */}
          <div className="md:w-2/3">
            <div className="flex items-center mb-6">
              <button 
                onClick={() => navigate('/dashboard')}
                className="mr-4 p-2 rounded-full hover:bg-gray-100"
              >
                <ArrowLeft size={24} />
              </button>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold">Mental Imagery</h1>
                <p className="text-gray-600">Create and practice mental imagery sessions to enhance your performance</p>
              </div>

          </div>
          
          {error && (
            <div className="alert alert-error mb-6">
              <AlertCircle className="h-6 w-6" />
              <span>{error}</span>
            </div>
          )}
          
          {message && (
            <div className="alert alert-success mb-6">
              <CheckCircle className="h-6 w-6" />
              <span>{message}</span>
            </div>
          )}
          
          <div className="bg-white rounded-lg shadow-md p-3 sm:p-4 mb-6 desktop-standard-card">
            {uiState.hasExistingAudio ? (
              // Audio exists - show only audio player interface
              <div>
                <h2 className="text-xl font-bold mb-4">Your Mental Imagery Audio</h2>
                <div className="bg-gray-100 p-4 rounded-lg mb-4">
                  <div className="flex justify-between items-center mb-3">
                    <div className="flex items-center">
                      <button
                        onClick={isPlaying ? pauseAudio : playAudio}
                        className="bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-full mr-3"
                      >
                        {isPlaying ? <Pause size={20} /> : <Play size={20} />}
                      </button>
                      <div>
                        <h3 className="font-medium">Ready to Play</h3>
                        <p className="text-sm text-gray-600">Your personalized mental imagery session</p>
                      </div>
                    </div>
                    <button
                      onClick={downloadAudio}
                      className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-1 rounded flex items-center"
                    >
                      <Download size={16} className="mr-1" />
                      Download
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              // No audio - show script creation interface
              <div>
                <h2 className="text-xl font-bold mb-4">Create New Mental Imagery</h2>
                
                {uiState.showScriptFields && (
                  <>
                    <div className="mb-4">
                      <label className="block text-gray-700 text-sm font-bold mb-2">
                        Title
                      </label>
                      <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="Enter a title for your mental imagery session"
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                      />
                    </div>
                    
                    <div className="mb-4">
                      <div className="flex justify-between items-center mb-2">
                        <label className="block text-gray-700 text-sm font-bold">
                          Script
                        </label>
                        <button
                          onClick={generateMentalImageryScript}
                          disabled={generatingScript}
                          className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold py-1 px-3 rounded focus:outline-none focus:shadow-outline flex items-center"
                        >
                          {generatingScript ? (
                            <>
                              <Loader className="animate-spin mr-2" size={16} />
                              Generating...
                            </>
                          ) : (
                            <>
                              <RefreshCw className="mr-2" size={16} />
                              Generate Script
                            </>
                          )}
                        </button>
                      </div>
                      <div className="relative">
                        <textarea
                          value={scriptInput}
                          onChange={(e) => {
                            if (e.target.value.length <= 4000) {
                              setScriptInput(e.target.value);
                            }
                          }}
                          placeholder="Enter or generate your mental imagery script here..."
                          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline h-40 resize-none"
                        />
                        <div className="absolute bottom-2 right-2 text-xs text-gray-500">
                          {scriptInput.length}/{MAX_SCRIPT_LENGTH}
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
            
            {/* Audio Settings - show only when creating new audio */}
            {uiState.showScriptFields && (
              <div className="mb-4">
                <button
                  onClick={() => setShowSettings(!showSettings)}
                  className="flex items-center space-x-2 text-gray-700 hover:text-blue-600"
                >
                  <Settings size={16} />
                  <span>Audio Settings</span>
                  <span className="text-xs">{showSettings ? '▼' : '▶'}</span>
                </button>
                
                {showSettings && (
                  <div className="mt-3 p-3 bg-gray-100 rounded-lg">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-gray-700 text-xs font-bold mb-1">Voice</label>
                        <select
                          value={audioSettings.voice}
                          onChange={(e) => setAudioSettings({...audioSettings, voice: e.target.value})}
                          className="w-full text-xs border rounded px-2 py-1"
                        >
                          <option value="calm">Calm</option>
                          <option value="energetic">Energetic</option>
                          <option value="soothing">Soothing</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-gray-700 text-xs font-bold mb-1">Background</label>
                        <select
                          value={audioSettings.background}
                          onChange={(e) => setAudioSettings({...audioSettings, background: e.target.value})}
                          className="w-full text-xs border rounded px-2 py-1"
                        >
                          <option value="none">None</option>
                          <option value="ambient">Ambient</option>
                          <option value="nature">Nature</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-gray-700 text-xs font-bold mb-1">Pace</label>
                        <select
                          value={audioSettings.pace}
                          onChange={(e) => setAudioSettings({...audioSettings, pace: e.target.value})}
                          className="w-full text-xs border rounded px-2 py-1"
                        >
                          <option value="slow">Slow</option>
                          <option value="medium">Medium</option>
                          <option value="fast">Fast</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-gray-700 text-xs font-bold mb-1">Duration (min)</label>
                        <div className="flex items-center space-x-1">
                          <button
                            onClick={() => setAudioSettings({...audioSettings, duration: Math.max(5, audioSettings.duration - 1)})}
                            className="bg-gray-200 hover:bg-gray-300 text-xs px-2 py-1 rounded"
                          >
                            <Minus size={12} />
                          </button>
                          <span className="text-xs px-2">{audioSettings.duration}</span>
                          <button
                            onClick={() => setAudioSettings({...audioSettings, duration: Math.min(20, audioSettings.duration + 1)})}
                            className="bg-gray-200 hover:bg-gray-300 text-xs px-2 py-1 rounded"
                          >
                            <Plus size={12} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            <div className="mt-6 space-y-4">
              <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
                <button
                  onClick={generateAudio}
                  disabled={generatingAudio || !title || !scriptInput}
                  className={`flex-1 bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-lg focus:outline-none focus:shadow-outline flex items-center justify-center ${generatingAudio || !title || !scriptInput ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {generatingAudio ? (
                    <>
                      <Loader className="animate-spin mr-2" size={20} />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Volume2 className="mr-2" size={20} />
                      Generate Audio
                    </>
                  )}
                </button>
                
                {recordingPermission ? (
                  <button
                    onClick={isRecording ? stopRecording : startRecording}
                    className={`flex-1 ${isRecording ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'} text-white font-bold py-3 px-4 rounded-lg focus:outline-none focus:shadow-outline flex items-center justify-center`}
                  >
                    {isRecording ? (
                      <>
                        <StopCircle className="mr-2" size={20} />
                        Stop ({formatTime(recordingTime)})
                      </>
                    ) : (
                      <>
                        <Mic className="mr-2" size={20} />
                        Record My Voice
                      </>
                    )}
                  </button>
                ) : (
                  <button
                    onClick={requestRecordingPermission}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-4 rounded-lg focus:outline-none focus:shadow-outline flex items-center justify-center"
                  >
                    <MicOff className="mr-2" size={20} />
                    Enable Recording
                  </button>
                )}
                
                <button
                  onClick={saveMentalImagery}
                  disabled={!title || !scriptInput}
                  className={`flex-1 bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-4 rounded-lg focus:outline-none focus:shadow-outline flex items-center justify-center ${!title || !scriptInput ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <Save className="mr-2" size={20} />
                  Save to Library
                </button>
              </div>
            </div>
            
            {/* Enhanced Loading Indicator for Audio Generation */}
            {generatingAudio && (
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-blue-500 rounded-lg p-6 mb-6 shadow-md">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <div className="relative">
                      <Loader size={24} className="animate-spin text-blue-600 mr-3" />
                      <div className="absolute inset-0 rounded-full border-2 border-blue-200"></div>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-blue-900">Generating Your Mental Imagery Audio</h3>
                      <p className="text-blue-700 text-sm">This process typically takes 30-90 seconds...</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-blue-600">🎧</div>
                  </div>
                </div>
                
                <div className="bg-white bg-opacity-60 rounded-lg p-4 mb-4">
                  <div className="flex items-center justify-between text-sm text-blue-800 mb-2">
                    <span>Processing your script with AI voice synthesis...</span>
                    <span className="font-medium">Please wait</span>
                  </div>
                  <div className="w-full bg-blue-200 rounded-full h-2">
                    <div className="bg-blue-600 h-2 rounded-full animate-pulse" style={{width: '45%'}}></div>
                  </div>
                </div>
                
                <div className="flex items-center justify-between text-sm text-blue-700">
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mr-2 animate-pulse"></div>
                    <span>Converting text to high-quality audio...</span>
                  </div>
                  <span className="text-xs bg-blue-100 px-2 py-1 rounded-full">
                    Stay on this page
                  </span>
                </div>
                
                <div className="mt-4 p-3 bg-blue-100 bg-opacity-50 rounded-lg">
                  <p className="text-xs text-blue-600 flex items-center">
                    <AlertCircle size={14} className="mr-1" />
                    <strong>Tip:</strong> Longer scripts may take up to 2 minutes. Your audio will appear automatically when ready.
                  </p>
                </div>
              </div>
            )}
            
            {recordedAudio && (
              <div className="bg-white rounded-lg shadow-md p-6 mb-6 desktop-standard-card mt-6">
                <h3 className="text-lg font-bold mb-4">Your Recording</h3>
                <div className="bg-gray-100 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <button
                        onClick={() => {
                          const audio = new Audio(recordedAudio);
                          audio.play();
                        }}
                        className="bg-blue-600 hover:bg-blue-700 text-white p-2 rounded-full mr-3"
                      >
                        <Play size={16} />
                      </button>
                      <span className="text-gray-700">Recorded Audio ({formatTime(recordingTime)})</span>
                    </div>
                    <button
                      onClick={() => {
                        const link = document.createElement('a');
                        link.href = recordedAudio;
                        link.download = `${title || 'mental-imagery'}-recording.webm`;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                      }}
                      className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded flex items-center"
                    >
                      <Download size={16} className="mr-1" />
                      Download
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {(audioUrl && audioUrl !== "pending" && audioUrl !== "") || recordedAudio ? (
            <div className="bg-white rounded-lg shadow-md p-3 sm:p-4 mb-6 desktop-standard-card">
              <h2 className="text-xl font-bold mb-4">Audio Player</h2>
              
              <div className="bg-gray-100 p-4 rounded-lg">
                <div className="flex justify-between items-center mb-3">
                  <div className="flex items-center">
                    <button
                      onClick={togglePlayPause}
                      className="bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-full mr-3"
                      disabled={!audioUrl || audioUrl === "pending"}
                    >
                      {isPlaying ? <Pause size={20} /> : <Play size={20} />}
                    </button>
                    <div>
                      <h3 className="font-medium">{title || "Untitled Session"}</h3>
                      <p className="text-sm text-gray-600">{audioSettings.duration} minutes</p>
                    </div>
                  </div>
                </div>
                
                {(audioUrl && audioUrl !== "pending" && audioUrl !== "") || recordedAudio ? (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-gray-600">{formatTime(currentTime)}</span>
                      <span className="text-gray-600">{formatTime(duration)}</span>
                    </div>
                    <div className="w-full bg-gray-300 rounded-full h-1.5 mb-4">
                      <div
                        className="bg-blue-600 h-1.5 rounded-full"
                        style={{ width: `${(currentTime / duration) * 100}%` }}
                      ></div>
                    </div>
                    <audio
                      ref={audioRef}
                      src={recordedAudio || audioUrl}
                      onTimeUpdate={handleTimeUpdate}
                      onLoadedMetadata={handleLoadedMetadata}
                      onEnded={handleAudioEnded}
                      className="hidden"
                    />
                  </div>
                ) : (
                  <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
                    <p className="text-yellow-700 flex items-center">
                      <AlertCircle size={18} className="mr-2" />
                      Audio is being prepared. You can still edit your script while waiting.
                    </p>
                  </div>
                )}
              </div>
              
              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4 mt-4">
                {((audioUrl && audioUrl !== "pending" && audioUrl !== "") || recordedAudio) && (
                  <button
                    onClick={downloadAudio}
                    className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline flex items-center justify-center"
                  >
                    <Download className="mr-2" size={20} />
                    Download Audio
                  </button>
                )}
              </div>
            </div>
          ) : null}
        </div>
        
        {/* Sidebar */}
        <div className="md:w-1/3">
          <div className="bg-white rounded-lg shadow-md p-3 sm:p-4 mb-6 sticky top-4 desktop-standard-card">
            <h2 className="text-xl font-bold mb-4">My Mental Imagery Library</h2>
            
            {loading ? (
              <div className="flex justify-center items-center p-8">
                <Loader className="h-8 w-8 animate-spin text-blue-500" />
              </div>
            ) : savedMentalImagery.length > 0 ? (
              <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2">
                {savedMentalImagery.map(imagery => (
                  <div 
                    key={imagery._id} 
                    className={`bg-gray-100 p-4 rounded-lg cursor-pointer ${
                      currentMentalImagery && currentMentalImagery._id === imagery._id ? 'border-2 border-blue-500' : ''
                    }`}
                    onClick={() => fetchMentalImageryById(imagery._id)}
                  >
                    <div className="flex justify-between items-center mb-3">
                      <div>
                        <h3 className="font-medium">{imagery.title}</h3>
                        <p className="text-sm text-gray-600">
                          {new Date(imagery.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteMentalImageryById(imagery._id);
                        }}
                        className="text-red-500 hover:text-red-700 p-2"
                      >
                        <Trash2 size={20} />
                      </button>
                    </div>
                    
                    <div className="flex items-center space-x-2 mt-2">
                      {imagery.audioUrl && !imagery.isScriptOnly && imagery.audioUrl !== "pending" ? (
                        // Audio exists - show Play button
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            // Implement play functionality for saved audios
                            let audioUrlToPlay = imagery.audioUrl;
                            if (!audioUrlToPlay) {
                              setError('Audio URL is missing');
                              return;
                            }
                            
                            // Handle relative URLs
                            if (audioUrlToPlay.startsWith('/')) {
                              audioUrlToPlay = `${API_URL}${audioUrlToPlay}`;
                            }
                            
                            // Validate that URL is properly formed
                            try {
                              new URL(audioUrlToPlay);
                            } catch (e) {
                              console.error('Invalid audio URL:', audioUrlToPlay, e);
                              setError('Invalid audio URL format');
                              return;
                            }
                            
                            console.log('Playing audio from URL:', audioUrlToPlay);
                            setAudioUrl(audioUrlToPlay);
                            setIsPlaying(false);
                            setTimeout(() => {
                              playAudio();
                            }, 100);
                          }}
                          className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded flex items-center justify-center"
                        >
                          <Play size={16} className="mr-1" />
                          Play
                        </button>
                      ) : (
                        // Script-only - show Generate Audio button
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            generateAudioForScript(imagery._id);
                          }}
                          disabled={generatingAudio}
                          className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-3 py-1 rounded flex items-center justify-center"
                        >
                          {generatingAudio ? (
                            <>
                              <Loader size={16} className="mr-1 animate-spin" />
                              Generating...
                            </>
                          ) : (
                            <>
                              <Volume2 size={16} className="mr-1" />
                              Generate Audio
                            </>
                          )}
                        </button>
                      )}
                      <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                        {audioSettings?.duration || imagery.settings?.duration || 5} min
                      </span>
                      {imagery.isScriptOnly && (
                        <span className="px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-800">
                          Script Only
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-gray-50 p-6 rounded-lg text-center">
                <Volume2 size={48} className="text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">
                  You haven't created any mental imagery sessions yet. Create your first one!
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
    </>
   );
}
export default MentalImagery;